import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Box, Stack,
  Typography, IconButton, Checkbox, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { ApiEndpointAttrs, ApiParam, ApiResponse } from '@atlas/docs';

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const PARAM_IN = ['path', 'query', 'header', 'body'];

const EMPTY: ApiEndpointAttrs = { method: 'GET', baseUrl: '', path: '', summary: '', description: '', auth: '', params: [], requestBody: { contentType: 'application/json', example: '' }, responses: [] };
const MONO = { '& input, & textarea': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 } };

// Friendly form to author/edit an inline API-method block (no JSON).
export function ApiBlockDialog({
  open, initial, onClose, onSave,
}: { open: boolean; initial: Partial<ApiEndpointAttrs> | null; onClose: () => void; onSave: (attrs: ApiEndpointAttrs) => void }) {
  const [f, setF] = useState<ApiEndpointAttrs>(EMPTY);
  useEffect(() => {
    if (open) setF({
      ...EMPTY, ...(initial ?? {}),
      params: [...(initial?.params ?? [])],
      responses: [...(initial?.responses ?? [])],
      requestBody: { ...EMPTY.requestBody, ...(initial?.requestBody ?? {}) },
    });
  }, [open, initial]);

  const set = (k: keyof ApiEndpointAttrs, v: any) => setF((s) => ({ ...s, [k]: v }));
  const setParam = (i: number, k: keyof ApiParam, v: any) => set('params', f.params.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const setResp = (i: number, k: keyof ApiResponse, v: any) => set('responses', f.responses.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>API endpoint</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1.5}>
            <TextField select label="Method" value={f.method} onChange={(e) => set('method', e.target.value)} sx={{ width: 130 }}>
              {METHODS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
            <TextField label="Path" value={f.path} onChange={(e) => set('path', e.target.value)} placeholder="/api/v2/users/{id}" sx={{ flex: 1, ...MONO }} />
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <TextField label="Base URL" value={f.baseUrl} onChange={(e) => set('baseUrl', e.target.value)} placeholder="https://api.cymmetri.io" sx={{ flex: 1, ...MONO }} />
            <TextField label="Authorization" value={f.auth} onChange={(e) => set('auth', e.target.value)} placeholder="Bearer <token>" sx={{ flex: 1, ...MONO }} />
          </Stack>
          <TextField label="Summary" value={f.summary} onChange={(e) => set('summary', e.target.value)} fullWidth />
          <TextField label="Description" value={f.description} onChange={(e) => set('description', e.target.value)} fullWidth multiline minRows={2} />

          <Section title="Parameters" onAdd={() => set('params', [...f.params, { name: '', in: 'query', type: 'string', required: false, description: '' }])}>
            {f.params.map((p, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                <TextField size="small" label="Name" value={p.name} onChange={(e) => setParam(i, 'name', e.target.value)} sx={{ flex: 1 }} />
                <TextField size="small" select label="In" value={p.in} onChange={(e) => setParam(i, 'in', e.target.value)} sx={{ width: 100 }}>
                  {PARAM_IN.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Type" value={p.type} onChange={(e) => setParam(i, 'type', e.target.value)} sx={{ width: 110 }} />
                <Tooltip title="Required"><Checkbox checked={p.required} onChange={(e) => setParam(i, 'required', e.target.checked)} /></Tooltip>
                <TextField size="small" label="Description" value={p.description} onChange={(e) => setParam(i, 'description', e.target.value)} sx={{ flex: 2 }} />
                <IconButton size="small" color="error" onClick={() => set('params', f.params.filter((_, j) => j !== i))}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
              </Stack>
            ))}
          </Section>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Request body</Typography>
            <Stack spacing={1}>
              <TextField size="small" label="Content-Type" value={f.requestBody.contentType} onChange={(e) => set('requestBody', { ...f.requestBody, contentType: e.target.value })} sx={{ width: 240 }} />
              <TextField size="small" label="Example (JSON)" value={f.requestBody.example} onChange={(e) => set('requestBody', { ...f.requestBody, example: e.target.value })} fullWidth multiline minRows={3} sx={MONO} placeholder={'{\n  "name": "Alex"\n}'} />
            </Stack>
          </Box>

          <Section title="Responses" onAdd={() => set('responses', [...f.responses, { status: '200', description: '', example: '' }])}>
            {f.responses.map((r, i) => (
              <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField size="small" label="Status" value={r.status} onChange={(e) => setResp(i, 'status', e.target.value)} sx={{ width: 110 }} />
                  <TextField size="small" label="Description" value={r.description} onChange={(e) => setResp(i, 'description', e.target.value)} sx={{ flex: 1 }} />
                  <IconButton size="small" color="error" onClick={() => set('responses', f.responses.filter((_, j) => j !== i))}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
                </Stack>
                <TextField size="small" label="Example response (JSON)" value={r.example ?? ''} onChange={(e) => setResp(i, 'example', e.target.value)} fullWidth multiline minRows={2} sx={{ mt: 1, ...MONO }} />
              </Box>
            ))}
          </Section>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!f.path.trim()} onClick={() => onSave(f)}>Insert</Button>
      </DialogActions>
    </Dialog>
  );
}

function Section({ title, onAdd, children }: { title: string; onAdd: () => void; children: React.ReactNode }) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={onAdd}>Add</Button>
      </Stack>
      <Stack spacing={1}>{children}</Stack>
    </Box>
  );
}
